const API_BASE = (import.meta.env.VITE_API_BASE as string) || '';

const DEFAULT_GET_TIMEOUT_MS = 15_000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 30_000;

export class ApiTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiTimeoutError';
  }
}

export interface SketchSummary {
  id: string;
  name: string;
  tempo_bpm: number | null;
  created_at: string;
  updated_at: string;
}

export interface ServerTrack {
  id: string;
  sketch_id: string;
  user_id: number;
  mime_type: string;
  duration_ms: number | null;
  order_idx: number;
  offset_ms: number;
  start_at_ms: number;
  gain: number;
  pan: number;
  muted?: boolean;
  created_at: string;
  audio_url: string;
}

export interface FullSketch {
  sketch: SketchSummary;
  tracks: ServerTrack[];
}

export interface UploadResponse {
  ok: boolean;
  sketch_id: string;
  sketch_name: string;
  track_id: string;
  track_count: number;
}

function authHeaders(initData: string): HeadersInit {
  return initData ? { 'X-Telegram-Init-Data': initData } : {};
}

interface FetchOpts {
  timeoutMs: number;
  signal?: AbortSignal;
  label: string;
}

async function fetchWithTimeout(input: RequestInfo, init: RequestInit, opts: FetchOpts): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs);
  const onExternalAbort = () => ctrl.abort();
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort();
    else opts.signal.addEventListener('abort', onExternalAbort, { once: true });
  }
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') {
      if (opts.signal?.aborted) throw e;
      throw new ApiTimeoutError(`${opts.label} timed out after ${opts.timeoutMs}ms`);
    }
    throw e;
  } finally {
    clearTimeout(timer);
    if (opts.signal) opts.signal.removeEventListener('abort', onExternalAbort);
  }
}

export async function fetchSketch(id: string, initData: string, signal?: AbortSignal): Promise<FullSketch> {
  const res = await fetchWithTimeout(
    `${API_BASE}/api/sketches/${encodeURIComponent(id)}`,
    { headers: authHeaders(initData) },
    { timeoutMs: DEFAULT_GET_TIMEOUT_MS, signal, label: `fetch sketch ${id}` }
  );
  if (!res.ok) throw new Error(`fetch sketch ${id}: ${res.status}`);
  return res.json() as Promise<FullSketch>;
}

export async function fetchStem(audioUrl: string, initData: string, signal?: AbortSignal): Promise<ArrayBuffer> {
  const res = await fetchWithTimeout(
    `${API_BASE}${audioUrl}`,
    { headers: authHeaders(initData) },
    { timeoutMs: DEFAULT_GET_TIMEOUT_MS, signal, label: `fetch stem` }
  );
  if (!res.ok) throw new Error(`fetch stem: ${res.status}`);
  return res.arrayBuffer();
}

export async function uploadTrack(args: {
  initData: string;
  sketchId: string | null;
  sketchName: string | null;
  stem: Blob;
  durationMs: number;
  offsetMs?: number;
  startAtMs?: number;
  tempoBpm?: number;
  signal?: AbortSignal;
}): Promise<UploadResponse> {
  const form = new FormData();
  form.append('audio', args.stem, `stem.${extOf(args.stem.type)}`);
  if (args.sketchId) form.append('sketch_id', args.sketchId);
  if (args.sketchName) form.append('sketch_name', args.sketchName);
  form.append('duration_ms', String(args.durationMs));
  if (typeof args.offsetMs === 'number' && args.offsetMs !== 0) {
    form.append('offset_ms', String(args.offsetMs));
  }
  if (typeof args.startAtMs === 'number' && args.startAtMs > 0) {
    form.append('start_at_ms', String(Math.round(args.startAtMs)));
  }
  if (typeof args.tempoBpm === 'number') {
    form.append('tempo_bpm', String(args.tempoBpm));
  }

  const res = await fetchWithTimeout(
    `${API_BASE}/api/sketches/upload`,
    {
      method: 'POST',
      headers: authHeaders(args.initData),
      body: form,
    },
    { timeoutMs: DEFAULT_UPLOAD_TIMEOUT_MS, signal: args.signal, label: 'upload' }
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`upload ${res.status}: ${txt.slice(0, 200)}`);
  }
  return res.json() as Promise<UploadResponse>;
}

export async function updateTrackOffset(args: {
  initData: string;
  sketchId: string;
  trackId: string;
  offsetMs: number;
  signal?: AbortSignal;
}): Promise<void> {
  const res = await fetchWithTimeout(
    `${API_BASE}/api/sketches/${encodeURIComponent(args.sketchId)}/tracks/${encodeURIComponent(args.trackId)}/offset`,
    {
      method: 'PATCH',
      headers: { ...authHeaders(args.initData), 'Content-Type': 'application/json' },
      body: JSON.stringify({ offset_ms: args.offsetMs }),
    },
    { timeoutMs: DEFAULT_GET_TIMEOUT_MS, signal: args.signal, label: 'offset update' }
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`offset update ${res.status}: ${txt.slice(0, 200)}`);
  }
}

export async function updateTrackGain(args: {
  initData: string;
  sketchId: string;
  trackId: string;
  gain: number;
  signal?: AbortSignal;
}): Promise<void> {
  const res = await fetchWithTimeout(
    `${API_BASE}/api/sketches/${encodeURIComponent(args.sketchId)}/tracks/${encodeURIComponent(args.trackId)}/gain`,
    {
      method: 'PATCH',
      headers: { ...authHeaders(args.initData), 'Content-Type': 'application/json' },
      body: JSON.stringify({ gain: args.gain }),
    },
    { timeoutMs: DEFAULT_GET_TIMEOUT_MS, signal: args.signal, label: 'gain update' }
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`gain update ${res.status}: ${txt.slice(0, 200)}`);
  }
}

export async function updateTrackPan(args: {
  initData: string;
  sketchId: string;
  trackId: string;
  pan: number;
  signal?: AbortSignal;
}): Promise<void> {
  const res = await fetchWithTimeout(
    `${API_BASE}/api/sketches/${encodeURIComponent(args.sketchId)}/tracks/${encodeURIComponent(args.trackId)}/pan`,
    {
      method: 'PATCH',
      headers: { ...authHeaders(args.initData), 'Content-Type': 'application/json' },
      body: JSON.stringify({ pan: args.pan }),
    },
    { timeoutMs: DEFAULT_GET_TIMEOUT_MS, signal: args.signal, label: 'pan update' }
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`pan update ${res.status}: ${txt.slice(0, 200)}`);
  }
}

export async function updateTrackStartAt(args: {
  initData: string;
  trackId: string;
  startAtMs: number;
  signal?: AbortSignal;
}): Promise<void> {
  const res = await fetchWithTimeout(
    `${API_BASE}/api/tracks/${encodeURIComponent(args.trackId)}/start-at`,
    {
      method: 'PATCH',
      headers: { ...authHeaders(args.initData), 'Content-Type': 'application/json' },
      body: JSON.stringify({ start_at_ms: Math.round(args.startAtMs) }),
    },
    { timeoutMs: DEFAULT_GET_TIMEOUT_MS, signal: args.signal, label: 'start-at update' }
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`start-at update ${res.status}: ${txt.slice(0, 200)}`);
  }
}

function extOf(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('mpeg')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  return 'bin';
}
